import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import { Box, IconButton, TableCell, TableRow } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useFormContext } from 'react-hook-form'

import { ControlledTextField } from 'components/common'
import { representsOutOfHours } from 'constants/EntityConstants/OtherChargeEntity'
import { BookingChargeType } from 'generated/graphql'
import { usePermission } from 'providers/PermissionProvider'
import { TBookingForm } from 'types/form-types'

const ChargeNameTableCell = styled(TableCell)(({ theme }) => ({
  paddingRight: theme.spacing(1),
  minWidth: 120,
}))

const EditableTableCell = styled(TableCell)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  width: theme.spacing(15),
  minWidth: 120,
}))

const ActionTableCell = styled(TableCell)({
  padding: 'unset',
})

interface IProps {
  row: Record<any, any>
  index: number
  disabled?: boolean
  isAllowedToViewVatCategories: boolean
  isAllowedToDeleteCharge: boolean
  isAllowedToEditCharge: boolean
  showActionsColumn: boolean
  onRemoveClick: (index: number, item: Record<any, any>) => void
  onActivateClick: (index: number, item: Record<any, any>) => void
  onInfoClick: (index: number) => void
}

const ChargeRow = (props: IProps) => {
  const {
    row,
    index,
    disabled,
    isAllowedToViewVatCategories,
    isAllowedToDeleteCharge,
    isAllowedToEditCharge,
    showActionsColumn,
    onRemoveClick,
    onActivateClick,
    onInfoClick,
  } = props
  const { control } = useFormContext<TBookingForm>()
  const [isAllowedToEditCharges] = usePermission('EditBookingCharge')

  const handleRemoveClick = () => {
    onRemoveClick(index, row)
  }

  const handleActivateClick = () => {
    onActivateClick(index, row)
  }

  const handleInfoClick = () => {
    onInfoClick(index)
  }

  return (
    <TableRow>
      <ChargeNameTableCell>{row?.name}</ChargeNameTableCell>

      <EditableTableCell>
        {isAllowedToEditCharges ? (
          <ControlledTextField
            key={row.chargeKey}
            readOnly={row?.type === BookingChargeType.WaitAndReturn || representsOutOfHours(row)}
            control={control}
            label='Qty'
            name={`charges.${index}.quantity`}
            defaultValue={row?.quantity}
            disabled={disabled}
            type='text'
            inputMode='numeric'
            pattern='[0-9]*'
            sanitizedNumber
            min='0'
            size='small'
          />
        ) : (
          row?.quantity
        )}
      </EditableTableCell>

      <TableCell>
        <Box
          component='span'
          sx={{ color: row?.isAutomatic ? 'primary.main' : 'text.disabled', fontWeight: 500 }}>
          {row?.isAutomatic ? 'Yes' : 'No'}
        </Box>
      </TableCell>

      <TableCell>
        <Box
          component='span'
          sx={{ color: row.isDeleted ? 'text.disabled' : 'success.main', fontWeight: 500 }}>
          {row.isDeleted ? 'Disabled' : 'Active'}
        </Box>
      </TableCell>

      <EditableTableCell>
        {isAllowedToEditCharges && row?.type === BookingChargeType.Custom ? (
          <ControlledTextField
            key={row.chargeKey}
            control={control}
            label='Rate'
            name={`charges.${index}.rate`}
            defaultValue={row?.rate}
            disabled={disabled}
            type='text'
            inputMode='numeric'
            pattern='[0-9]*'
            sanitizedNumber
            min='0'
            size='small'
          />
        ) : (
          row?.rate
        )}
      </EditableTableCell>

      {showActionsColumn && (
        <ActionTableCell align='right'>
          <Box display='flex' justifyContent='flex-end'>
            {isAllowedToViewVatCategories && (
              <IconButton disabled={disabled} onClick={handleInfoClick} size='large'>
                <InfoIcon />
              </IconButton>
            )}

            {isAllowedToDeleteCharge && !row.isDeleted && (
              <IconButton disabled={disabled} onClick={handleRemoveClick} size='large'>
                <CloseIcon />
              </IconButton>
            )}

            {isAllowedToEditCharge && row.isAutomatic && row.isDeleted && (
              <IconButton
                disabled={disabled && !row.isDeleted}
                onClick={handleActivateClick}
                size='large'>
                <CheckIcon />
              </IconButton>
            )}
          </Box>
        </ActionTableCell>
      )}
    </TableRow>
  )
}

export default ChargeRow
